import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiBox,
  FiDollarSign,
  FiDownload,
  FiLayers,
  FiPackage,
  FiSearch,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const number = (value) => Number(value || 0);

const quantity = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });

const money = (value) =>
  `₹${number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

export default function StockValuationPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [balances, setBalances] = useState([]);
  const [search, setSearch] = useState("");
  const [storeId, setStoreId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadBalances = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const response = await apiRequest("/inventory/balances", {
        accessToken,
      });

      setBalances(response || []);
    } catch (err) {
      setError(err.message || "Unable to load stock valuation.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadBalances();
  }, [isAuthLoading, accessToken, loadBalances]);

  const stores = useMemo(() => {
    const unique = new Map();

    balances.forEach((balance) => {
      if (balance.store) unique.set(balance.store.id, balance.store);
    });

    return [...unique.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [balances]);

  const brands = useMemo(() => {
    const unique = new Map();

    balances.forEach((balance) => {
      const brand = balance.variant?.product?.brand;
      if (brand) unique.set(brand.id, brand);
    });

    return [...unique.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [balances]);

  const categories = useMemo(() => {
    const unique = new Map();

    balances.forEach((balance) => {
      const category = balance.variant?.product?.category;
      if (category) unique.set(category.id, category);
    });

    return [...unique.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [balances]);

  const filteredBalances = useMemo(() => {
    const term = search.trim().toLowerCase();

    return balances.filter((balance) => {
      const product = balance.variant?.product;

      const matchesSearch =
        !term ||
        product?.name?.toLowerCase().includes(term) ||
        balance.variant?.sku?.toLowerCase().includes(term);

      return (
        matchesSearch &&
        (!storeId || balance.storeId === storeId) &&
        (!brandId || product?.brandId === brandId) &&
        (!categoryId || product?.categoryId === categoryId)
      );
    });
  }, [balances, search, storeId, brandId, categoryId]);

  const summary = useMemo(() => {
    const skuIds = new Set();

    const result = filteredBalances.reduce(
      (total, balance) => {
        const onHand = number(balance.quantityOnHand);
        const averageCost = number(balance.averageUnitCost);

        if (onHand > 0) skuIds.add(balance.variantId);

        return {
          units: total.units + onHand,
          value: total.value + onHand * averageCost,
        };
      },
      { units: 0, value: 0 },
    );

    return {
      ...result,
      averageCost: result.units > 0 ? result.value / result.units : 0,
      skuCount: skuIds.size,
    };
  }, [filteredBalances]);

  const storeValue = useMemo(() => {
    const grouped = new Map();

    filteredBalances.forEach((balance) => {
      const storeName = balance.store?.name || "Store";

      grouped.set(
        storeName,
        (grouped.get(storeName) || 0) +
          number(balance.quantityOnHand) * number(balance.averageUnitCost),
      );
    });

    return [...grouped.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredBalances]);

  const brandValue = useMemo(() => {
    const grouped = new Map();

    filteredBalances.forEach((balance) => {
      const brandName =
        balance.variant?.product?.brand?.name || "Unbranded";

      grouped.set(
        brandName,
        (grouped.get(brandName) || 0) +
          number(balance.quantityOnHand) * number(balance.averageUnitCost),
      );
    });

    return [...grouped.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredBalances]);

  const exportReport = () => {
    const headers = [
      "Product",
      "SKU",
      "Brand",
      "Category",
      "Store",
      "Quantity On Hand",
      "Average Unit Cost",
      "Inventory Value",
    ];

    const rows = filteredBalances.map((balance) => {
      const onHand = number(balance.quantityOnHand);
      const averageCost = number(balance.averageUnitCost);

      return [
        balance.variant?.product?.name || "",
        balance.variant?.sku || "",
        balance.variant?.product?.brand?.name || "—",
        balance.variant?.product?.category?.name || "—",
        balance.store?.name || "—",
        onHand,
        averageCost,
        onHand * averageCost,
      ];
    });

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `stock-valuation-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">Inventory / Reports</p>

          <h1 className="mt-1 text-3xl font-bold">
            Stock Valuation Report
          </h1>

          <p className="mt-2 text-gray-500">
            Current inventory value based on weighted average unit cost.
          </p>
        </div>

        <button
          onClick={exportReport}
          disabled={filteredBalances.length === 0}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-60"
        >
          <FiDownload />
          Export Report
        </button>
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <StatCard
          title="Inventory Value"
          value={loading ? "—" : money(summary.value)}
          icon={FiDollarSign}
          color="green"
        />

        <StatCard
          title="Stock Units"
          value={loading ? "—" : quantity(summary.units)}
          icon={FiPackage}
          color="blue"
        />

        <StatCard
          title="Avg Cost"
          value={loading ? "—" : money(summary.averageCost)}
          icon={FiBox}
          color="orange"
        />

        <StatCard
          title="SKUs in Stock"
          value={loading ? "—" : summary.skuCount}
          icon={FiLayers}
          color="purple"
        />
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5">
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product or SKU..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Stores</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>

          <select
            value={brandId}
            onChange={(event) => setBrandId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Brands</option>

            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>

          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Categories</option>

            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {[
                  "Product",
                  "SKU",
                  "Store",
                  "Qty",
                  "Avg Cost",
                  "Value",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-6 py-4 text-left text-xs uppercase text-gray-500 last:text-left"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading inventory valuation…
                  </td>
                </tr>
              ) : filteredBalances.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No stock balances found.
                  </td>
                </tr>
              ) : (
                filteredBalances.map((balance) => {
                  const onHand = number(balance.quantityOnHand);
                  const averageCost = number(balance.averageUnitCost);
                  const value = onHand * averageCost;

                  return (
                    <tr
                      key={balance.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium">
                          {balance.variant?.product?.name}
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          {balance.variant?.product?.brand?.name || "Unbranded"} ·{" "}
                          {balance.variant?.product?.category?.name || "—"}
                        </p>
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {balance.variant?.sku}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {balance.store?.name}
                      </td>

                      <td className="px-6 py-4 text-left">
                        {quantity(onHand)}
                      </td>

                      <td className="px-6 py-4 text-left">
                        {money(averageCost)}
                      </td>

                      <td className="px-6 py-4 text-left font-bold text-green-600">
                        {money(value)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ValueSummary
          title="Inventory Value By Store"
          values={storeValue}
        />

        <ValueSummary
          title="Inventory Value By Brand"
          values={brandValue}
        />
      </div>
    </div>
  );
}

function ValueSummary({ title, values }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <h3 className="mb-5 text-lg font-semibold">{title}</h3>

      {values.length === 0 ? (
        <p className="text-sm text-gray-500">No valuation data available.</p>
      ) : (
        <div className="space-y-4">
          {values.slice(0, 6).map((item) => (
            <div key={item.name} className="flex justify-between gap-4">
              <span className="truncate text-gray-700">{item.name}</span>

              <span className="shrink-0 font-bold">{money(item.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-600",
    purple: "bg-purple-50 text-purple-600",
  };

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-2 text-3xl font-bold">{value}</h3>
        </div>

        <div
          className={`flex h-14 w-14 items-center justify-center rounded-2xl ${colors[color]}`}
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}